import { useAuthService } from './services/useAuthService';
import { useSiteStore } from '~/stores/site.store';
import { useRgStore } from '~/stores/responsibleGaming.store';
import type {
  IBrandGetLimitsType,
  IBrandLimits,
  IUserGetLimitsType,
  IUserLimits,
  IWagerProgress,
} from '~/interfaces/dto/auth/response';
import { globalKeys } from 'assets/data/global-keys';

export const useResponsibleGamingConfig = async () => {
  const siteStore = useSiteStore();
  if (siteStore.getFeatureFlags.get('myaccount.responsiblegaming'))
    return await Promise.allSettled([useGetBrandLimits(), useGetBrandLocks()]);
};
export const useUserConfig = async () => {
  const siteStore = useSiteStore();
  const response = await Promise.allSettled([
    ...(siteStore.getFeatureFlags.get('myaccount.responsiblegaming') && [
      await useGetUserLimits(),
      await useGetWagerProgress(),
    ]),
    await useGetUserLocks(),
    await useGetUserRestrictions(),
  ]);
  useSessionTimeout();
  return response;
};

export const useResponsibleGamingInit = async () => {
  return await Promise.allSettled([
    useResponsibleGamingConfig(),
    useUserConfig(),
  ]);
};

export const sessionTimeout = () => useState('sessionTimeout', () => null);

export const useGetUserLimits = async (): Promise<
  Partial<IUserGetLimitsType>
> => {
  const $authService = useAuthService();
  return await $authService.getUserLimits();
};
export const useGetBrandLimits = async (): Promise<
  Partial<IBrandGetLimitsType>
> => {
  const $authService = useAuthService();
  return await $authService.getBrandLimits();
};
export const useGetBrandLocks = async (): Promise<void> => {
  const rg = useRgStore();
  const $authService = useAuthService();
  const brandLocks: Partial<IBrandGetLimitsType> =
    await $authService.getBrandLocks();
  useCheckForBrandExclusions(brandLocks?.data?.restrictionTypeResponses);
  const activeBrandLocks: IBrandLimits[] =
    brandLocks?.data?.restrictionTypeResponses;
  rg.setBrandLocks(activeBrandLocks);
};
export const useGetWagerProgress = async (): Promise<IWagerProgress> => {
  const siteStore = useSiteStore();
  if (siteStore.getFeatureFlags.get('responsiblegaming.wagerlimit')) {
    const $authService = useAuthService();
    return await $authService.getWagerProgress();
  }
};
export const useEndSessionTimeout = (): void => {
  const { $storageService } = useServices();
  const rg = useRgStore();

  clearInterval(sessionTimeout().value);
  sessionTimeout().value = null;
  rg.timerSet = false;
  $storageService.removeLocalStorage({
    key: globalKeys.authKeys.sessionTimer,
  });
};
export const useSessionTimeout = (): void => {
  const siteStore = useSiteStore();
  if (siteStore.getFeatureFlags.get('responsiblegaming.sessionlimit')) {
    const { $storageService } = useServices();
    const rg = useRgStore();
    const sessionLimit = ref<number>(rg.currentSessionLimit?.threshold);
    const totalMins = ref<number>(rg.currentTimer);
    const persistTimer: number =
      Number(
        $storageService.getLocalStorage({
          key: globalKeys.authKeys.sessionTimer,
        }),
      ) || null;

    if (!!sessionLimit.value && !!totalMins.value) {
      if (!rg.timerSet) {
        rg.setTimerSet(true);
        if (!persistTimer) {
          $storageService.setLocalStorage({
            key: globalKeys.authKeys.sessionTimer,
            data: rg.currentTimer,
          });
        } else {
          totalMins.value = persistTimer;
        }
        sessionTimeout().value = setInterval((): void => {
          if (totalMins.value === 0) {
            useEndSessionTimeout();
            siteStore.activateModal('sessionLimitExceeded');
          } else if (totalMins.value > 0) {
            totalMins.value -= 1;
            $storageService.setLocalStorage({
              key: globalKeys.authKeys.sessionTimer,
              data: totalMins.value,
            });
          }
        }, 1000);
      }
    } else {
      if (!!sessionTimeout().value) {
        useEndSessionTimeout();
      }
    }
  }
};

export const useGetUserLocks = async (): Promise<void> => {
  const rg = useRgStore();
  const $authService = useAuthService();
  const locksResponse: Partial<IUserGetLimitsType> =
    await $authService.getUserLocks();
  const userLocks = locksResponse?.data?.restrictionResponses;

  const activeUserLock: IUserLimits = userLocks?.find(
    (lock: IUserLimits): boolean => lock.isActive === true,
  );

  if (!!activeUserLock) {
    rg.setUserLocks(activeUserLock);
    if (!activeUserLock.restrictionType.restrictionPermissions.allowedLogin) {
      useAuthStore().logout();
      useSiteStore().activateModal('accountLocked');
    }
  } else {
    if (!rg.currentRestriction) rg.removeLocks();
  }
};

export const useGetUserRestrictions = async (): Promise<void> => {
  const rg = useRgStore();
  const $authService = useAuthService();
  const locksResponse: Partial<IUserGetLimitsType> =
    await $authService.getUserRestrictions();
  const userLocks = locksResponse?.data?.restrictionResponses;
  const activeUserLock: IUserLimits = userLocks?.find(
    (lock: IUserLimits): boolean => lock.isActive === true,
  );

  if (!!activeUserLock) {
    rg.setUserLocks(activeUserLock);
  } else {
    rg.removeLocks();
  }
};

export const useCheckForBrandExclusions = (complianceData: IBrandLimits[]) => {
  const rg = useRgStore();
  if (!!complianceData) {
    const brandExclusions = complianceData
      .filter((rtr) => rtr.name.includes('(Self Exclusion)'))
      .map((rtr) => {
        return {
          ...rtr,
          name: rtr.name
            .replace(' (Self Exclusion)', '')
            .replace(' Customer', '')
            .trim(),
        };
      });
    if (!!brandExclusions.length) rg.setExclusions(brandExclusions);
  }
};
